import {
  VictoryBar,
  VictoryChart,
  VictoryAxis,
  VictoryLabel,
} from 'victory';
import { useEffect, useRef, useState } from 'react';
import tailwindConfig from '../../../tailwind.config';

export interface HorizontalBarCategory {
  label: string;
  percentage: number;
  dataLabel: string;
  color: string;
}

interface HorizontalBarGraphProps {
  categories: HorizontalBarCategory[];
}

export default function HorizontalBarGraph({ categories }: HorizontalBarGraphProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [chartWidth, setChartWidth] = useState(0);
  const colours = tailwindConfig.theme.extend.colors;

  useEffect(() => {
    if (!containerRef.current) return undefined;
    const resizeObserver = new ResizeObserver((entries) => {
      if (!entries || entries.length === 0) return;
      const { width } = entries[0].contentRect;
      setChartWidth(width);
    });
    resizeObserver.observe(containerRef.current);
    return () => resizeObserver.disconnect();
  }, []);

  // Victory's horizontal bar has categorical axes bottom-up so we need to reverse the array
  const chartData = [...categories].map((cat) => ({
    x: cat.label,
    y: cat.percentage,
    color: cat.color,
    dataLabel: cat.dataLabel,
  }));

  // Extra bar graph data to show full width/length of the bar to represent 100%
  const backgroundData = chartData.map((d) => ({
    x: d.x,
    y: 100,
    percentage: d.y,
    dataLabel: d.dataLabel,
  }));
  const chartHeight = categories.length * 32 + 12; // row/bar height + padding
  const paddingLeft = Math.min(chartWidth * 0.25, 150);
  const paddingRight = Math.min(chartWidth * 0.15, 90);
  return (
    <div ref={containerRef} className="w-full">
      {chartWidth > 0 && (
      <VictoryChart
        horizontal
        width={chartWidth}
        height={chartHeight}
        padding={{
          top: 8,
          bottom: 8,
          left: paddingLeft,
          right: paddingRight,
        }}
        domainPadding={{ x: [8, 8] }}
        domain={{ y: [0, 100] }}
      >
        <VictoryAxis
          invertAxis
          style={{
            axis: { stroke: 'none' },
            tickLabels: {
              fontFamily: 'Nunito, sans-serif',
              fontSize: 13,
              fill: colours.piptext.strong,
              padding: 0,
            },
          }}
          tickLabelComponent={(
            <VictoryLabel
              textAnchor="start"
              x={8}
            />
            )}
        />
        <VictoryAxis
          dependentAxis
          style={{
            axis: { stroke: 'none' },
            grid: { stroke: 'none' },
            tickLabels: { fill: 'none' },
          }}
        />
        <VictoryBar
          data={backgroundData}
          barWidth={14}
          cornerRadius={{
            topLeft: 7,
            topRight: 7,
            bottomLeft: 7,
            bottomRight: 7,
          }}
          style={{
            data: { fill: colours.pipbg.grouped },
          }}
          labels={({ datum }) => datum.dataLabel}
          labelComponent={(
            <VictoryLabel
              textAnchor="end"
              dx={paddingRight - 15}
              style={{
                fontFamily: 'Nunito, sans-serif',
                fontSize: 13,
                fill: colours.piptext.strong,
              }}
            />
            )}
        />
        <VictoryBar
          data={chartData}
          barWidth={14}
          cornerRadius={{
            topLeft: 7,
            topRight: 7,
            bottomLeft: 7,
            bottomRight: 7,
          }}
          style={{
            data: {
              fill: ({ datum }) => datum.color,
            },
          }}
        />
      </VictoryChart>
      )}
    </div>
  );
}
